import { apiFetch } from '../../../api/crud';
import { APP_NOTIFICATIONS_PATH } from '../config/service';

export const NOTIFICATIONS_PAGE_SIZE = 10;

export const NotificationStatus = {
  UNREAD: 'UNREAD',
  READ: 'READ',
  HIDDEN: 'HIDDEN',
};

/** `section === 'news'` → News tab; otherwise Messages (trashie-mobile). */
export const NotificationSection = {
  NEWS: 'news',
};

function buildQuery(params = {}) {
  const query = new URLSearchParams();
  if (params.page != null) query.set('page', String(params.page));
  if (params.pageSize != null) query.set('pageSize', String(params.pageSize));
  if (params.status) query.set('status', String(params.status));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

function unwrapPayload(result) {
  const payload = result?.data ?? result?.result ?? result;
  const notifications = Array.isArray(payload?.notifications) ? payload.notifications : [];
  const total = typeof payload?.total === 'number' ? payload.total : notifications.length;
  return { notifications, total };
}

export function normalizeAppNotificationData(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return typeof raw === 'object' ? raw : {};
}

export function normalizeAppNotification(record) {
  if (!record) return null;
  return {
    ...record,
    data: normalizeAppNotificationData(record.data),
  };
}

function coerceDeeplinkString(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  return '';
}

function coerceDeeplinkFromDefaultActionData(raw) {
  if (raw == null) return '';
  if (typeof raw === 'string') {
    if (!raw.trim()) return '';
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return coerceDeeplinkString(
          parsed.data ?? parsed.deeplink ?? parsed.url ?? parsed.href ?? parsed.uri ?? '',
        );
      }
    } catch {
      return raw.trim();
    }
    return raw.trim();
  }
  if (typeof raw === 'object') {
    return coerceDeeplinkString(
      raw.data ?? raw.deeplink ?? raw.url ?? raw.href ?? raw.uri ?? raw.path ?? '',
    );
  }
  return coerceDeeplinkString(raw);
}

/**
 * Resolve tap target URL from notification payload (Iterable / Maestra / webhooks
 * may use defaultAction.data, deeplink, webPushClickAction, or top-level uri).
 */
export function getAppNotificationDeeplink(notification) {
  const data = notification?.data;
  if (!data || typeof data !== 'object') {
    return '';
  }

  let fromAction = '';
  if (data.defaultAction != null) {
    const da = data.defaultAction;
    if (typeof da === 'string') {
      try {
        const parsed = JSON.parse(da);
        fromAction = coerceDeeplinkFromDefaultActionData(parsed?.data ?? parsed);
      } catch {
        fromAction = coerceDeeplinkString(da);
      }
    } else if (typeof da === 'object') {
      fromAction = coerceDeeplinkFromDefaultActionData(da.data ?? da);
    }
  }

  const primary = coerceDeeplinkString(fromAction);
  if (primary) return primary;

  const fromDeeplink = coerceDeeplinkString(data.deeplink);
  if (fromDeeplink) return fromDeeplink;

  const fromWebPush = coerceDeeplinkString(data.webPushClickAction);
  if (fromWebPush) return fromWebPush;

  const fromUri = coerceDeeplinkString(data.uri);
  if (fromUri) return fromUri;

  const fromUrl = coerceDeeplinkString(data.url ?? data.link ?? data.clickUrl);
  if (fromUrl) return fromUrl;

  return '';
}

/**
 * Map Iterable / Maestra deeplinks to web navigation (matches trashie-mobile
 * useDeepLink: `trashie://segment/path` → `/segment/path`).
 *
 * `new URL('trashie://take-back-bags')` yields an empty pathname, which makes
 * navigateWithSearch('', …) a no-op — so trashie:// must be handled explicitly.
 *
 * @param {string} deeplink
 * @param {string} baseOrigin e.g. window.location.origin
 * @returns {object} `{ kind: 'empty' }` | `{ kind: 'external', href }` |
 *   `{ kind: 'internal', pathname, search }`
 */
export function splitNotificationDeeplinkForNavigation(deeplink, baseOrigin) {
  const s = coerceDeeplinkString(deeplink);
  if (!s) {
    return { kind: 'empty' };
  }
  if (/^https?:\/\//i.test(s)) {
    return { kind: 'external', href: s };
  }

  const qIndex = s.indexOf('?');
  const pathPart = qIndex === -1 ? s : s.slice(0, qIndex);
  const queryString = qIndex === -1 ? '' : s.slice(qIndex + 1);
  const searchFromQuery = Object.fromEntries(
    new URLSearchParams(queryString).entries(),
  );

  if (/^trashie:\/\//i.test(pathPart)) {
    let pathname = pathPart.replace(/^trashie:\/\//i, '/');
    pathname = pathname.replace(/\/{2,}/g, '/');
    if (!pathname || pathname === '/') {
      return { kind: 'empty' };
    }
    return { kind: 'internal', pathname, search: searchFromQuery };
  }

  const base = baseOrigin && baseOrigin.length > 0 ? baseOrigin : 'http://localhost';
  try {
    const url = new URL(s, base);
    if (!url.pathname || url.pathname === '') {
      return { kind: 'empty' };
    }
    const mergedSearch = {
      ...searchFromQuery,
      ...Object.fromEntries(url.searchParams.entries()),
    };
    return { kind: 'internal', pathname: url.pathname, search: mergedSearch };
  } catch {
    const pathname = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
    return { kind: 'internal', pathname, search: searchFromQuery };
  }
}

/**
 * GET /v1/app-notifications (trashie-mobile notificationsApi.fetchNotifications).
 * @param {{ page?: number, pageSize?: number, status?: string }} params
 */
export async function fetchAppNotifications(params = {}) {
  const path = `${APP_NOTIFICATIONS_PATH}${buildQuery({
    page: params.page ?? 1,
    pageSize: params.pageSize ?? NOTIFICATIONS_PAGE_SIZE,
    status: params.status,
  })}`;
  const result = await apiFetch({ path });
  return unwrapPayload(result);
}

/**
 * PUT /v1/app-notifications/:messageId/status
 * @param {string} messageId
 * @param {string} status — e.g. NotificationStatus.READ
 */
export async function updateAppNotificationStatus(messageId, status) {
  if (messageId == null || messageId === '') {
    return;
  }
  const path = `${APP_NOTIFICATIONS_PATH}/${encodeURIComponent(messageId)}/status`;
  await apiFetch({
    path,
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}
